import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, getAuthHeaders } from "../context/AuthContext";
import { Switch } from "./ui/switch";
import { ChartLineUp, Copy, Eye, EyeSlash, Crown, ArrowsClockwise, Trash, MagnifyingGlass, UserPlus, CheckCircle } from "@phosphor-icons/react";

const panel = "rounded-2xl p-5 bg-slate-900/60 border border-slate-800";

const copyText = (t, label) => {
  navigator.clipboard.writeText(t);
  toast.success(`${label} copied`);
};

export default function TradingViewAdmin() {
  const [settings, setSettings] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [showPwd, setShowPwd] = useState(false);

  const loadSettings = useCallback(() => {
    axios.get(`${API}/admin/tradingview/settings`, { headers: getAuthHeaders() })
      .then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const loadRows = useCallback((query = "") => {
    axios.get(`${API}/admin/tradingview/activations`, { params: { q: query }, headers: getAuthHeaders() })
      .then(r => { setRows(r.data.activations); setTotal(r.data.total); }).catch(() => {});
  }, []);

  useEffect(() => { loadSettings(); loadRows(); }, [loadSettings, loadRows]);
  useEffect(() => {
    const t = setTimeout(() => loadRows(q), 350);
    return () => clearTimeout(t);
  }, [q, loadRows]);

  const toggleOffer = async (v) => {
    try {
      await axios.put(`${API}/admin/tradingview/settings`, { lifetime_offer_enabled: v }, { headers: getAuthHeaders() });
      setSettings(s => ({ ...s, lifetime_offer_enabled: v }));
      toast.success(`Lifetime launch offer ${v ? "ENABLED" : "DISABLED"}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update");
    }
  };

  const retryGrant = async (userId) => {
    try {
      await axios.post(`${API}/admin/tradingview/users/${userId}/retry-grant`, {}, { headers: getAuthHeaders() });
      toast.success("Access granted on TradingView ✓");
      loadRows(q);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Grant failed");
      loadRows(q);
    }
  };

  const override = async (userId, action, label) => {
    if (action === "revoke" && !window.confirm("Revoke this user's TradingView access?")) return;
    try {
      await axios.post(`${API}/admin/tradingview/users/${userId}/override`, { action }, { headers: getAuthHeaders() });
      toast.success(label);
      loadRows(q);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    }
  };

  if (!settings) return <div className="text-slate-400 text-sm p-6">Loading...</div>;
  const m = settings.master || {};

  return (
    <div data-testid="tradingview-admin-section" className="space-y-5">
      <div className="flex items-center gap-3">
        <ChartLineUp size={26} className="text-cyan-400" />
        <div>
          <h2 className="text-xl font-bold text-white">TradingView Indicators</h2>
          <p className="text-xs text-slate-400">Veloxalgotech-HFT access management · {total} activated user{total !== 1 ? "s" : ""}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Lifetime offer toggle */}
        <div className={panel}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-bold text-sm text-white flex items-center gap-2">
                <Crown size={16} weight="fill" className="text-amber-400" /> Lifetime Launch Offer
              </p>
              <p className="text-xs text-slate-400 mt-1">
                ON → new activations get <b className="text-amber-300">LIFETIME</b> validity.<br />
                OFF → new activations get <b className="text-cyan-300">1 year</b> from activation date.
              </p>
            </div>
            <Switch data-testid="tv-lifetime-offer-toggle" checked={!!settings.lifetime_offer_enabled} onCheckedChange={toggleOffer} />
          </div>
          <p data-testid="tv-offer-status" className={`mt-3 text-xs font-bold ${settings.lifetime_offer_enabled ? "text-amber-300" : "text-slate-400"}`}>
            Current: {settings.lifetime_offer_enabled ? "LIFETIME (launch offer active)" : "1 YEAR validity"}
          </p>
        </div>

        {/* Master credentials */}
        <div className={panel} data-testid="tv-master-credentials">
          <p className="font-bold text-sm text-white mb-3">Master TradingView Account (grant access from here)</p>
          <div className="space-y-2 text-xs">
            <Row label="Username" value={m.username} onCopy={() => copyText(m.username, "Username")} />
            <div className="flex items-center justify-between gap-2">
              <span className="text-slate-400 w-24 shrink-0">Password</span>
              <span className="font-mono text-slate-200 truncate flex-1">{showPwd ? m.password : "••••••••••"}</span>
              <button data-testid="tv-pwd-toggle" onClick={() => setShowPwd(s => !s)} className="text-slate-400 hover:text-white">
                {showPwd ? <EyeSlash size={14} /> : <Eye size={14} />}
              </button>
              <button onClick={() => copyText(m.password, "Password")} className="text-slate-400 hover:text-white"><Copy size={14} /></button>
            </div>
            <Row label="Indicator" value={m.indicator_url} onCopy={() => copyText(m.indicator_url, "Indicator link")} link />
            <Row label="2FA Import" value={m.otp_link} onCopy={() => copyText(m.otp_link, "2FA link")} />
          </div>
          <p className="text-[10px] text-slate-500 mt-3">
            To grant access: open the indicator script → "Manage access" → add the user's TradingView ID from the table below.
          </p>
        </div>
      </div>

      {/* Manual add */}
      <ManualAddCard onAdded={() => loadRows(q)} />

      {/* Activations table */}
      <div className={panel}>
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <p className="font-bold text-sm text-white">Activated Users</p>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-3 top-2.5 text-slate-500" />
            <input
              data-testid="tv-search-input"
              value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search TV ID, name, email, VAT-ID..."
              className="pl-8 pr-3 py-2 rounded-lg text-xs bg-slate-950 border border-slate-800 text-white outline-none w-64"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-slate-500 text-left border-b border-slate-800">
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">TradingView ID</th>
                <th className="py-2 pr-3">Activated</th>
                <th className="py-2 pr-3">Validity</th>
                <th className="py-2 pr-3">TV Access</th>
                <th className="py-2 pr-3">Changes</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-500">No TradingView activations yet</td></tr>
              )}
              {rows.map(r => (
                <tr key={r.user_id} data-testid={`tv-row-${r.user_id}`} className="border-b border-slate-800/60 text-slate-300">
                  <td className="py-2.5 pr-3">
                    <p className="font-semibold text-white">{r.name}</p>
                    <p className="text-[10px] text-slate-500">{r.display_id} · {r.email}</p>
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className="font-mono text-cyan-300">{r.tv_username}</span>
                    <button onClick={() => copyText(r.tv_username, "TV ID")} className="ml-1.5 text-slate-500 hover:text-white align-middle"><Copy size={12} /></button>
                  </td>
                  <td className="py-2.5 pr-3">{r.activated_at?.slice(0, 10)}</td>
                  <td className="py-2.5 pr-3">
                    {r.expired
                      ? <span className="px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 font-bold text-[10px]">EXPIRED</span>
                      : r.lifetime
                        ? <span className="px-2 py-0.5 rounded-full bg-amber-400/15 text-amber-300 font-bold text-[10px]">LIFETIME</span>
                        : <span className="px-2 py-0.5 rounded-full bg-cyan-400/10 text-cyan-300 font-bold text-[10px]">Till {r.expires_at?.slice(0, 10)}</span>}
                  </td>
                  <td className="py-2.5 pr-3">
                    {r.grant_status === "granted"
                      ? <span data-testid={`tv-grant-status-${r.user_id}`} className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-bold text-[10px]">GRANTED ✓</span>
                      : r.grant_status === "failed"
                        ? <span data-testid={`tv-grant-status-${r.user_id}`} title={r.grant_error} className="px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-300 font-bold text-[10px] cursor-help">FAILED ✕</span>
                        : <span data-testid={`tv-grant-status-${r.user_id}`} className="px-2 py-0.5 rounded-full bg-slate-700/40 text-slate-400 font-bold text-[10px]">MANUAL</span>}
                  </td>
                  <td className="py-2.5 pr-3">
                    <span className={r.locked ? "text-rose-300 font-bold" : ""}>{r.changes_used}/{r.changes_max}</span>
                    {r.locked && <span className="ml-1 text-[10px] text-rose-400">LOCKED</span>}
                  </td>
                  <td className="py-2.5">
                    <div className="flex gap-1.5 flex-wrap">
                      {r.grant_status !== "granted" && (
                        <ActBtn testid={`tv-retry-grant-${r.user_id}`} title="Retry granting access on TradingView" onClick={() => retryGrant(r.user_id)}>
                          <ArrowsClockwise size={12} /> Grant on TV
                        </ActBtn>
                      )}
                      <ActBtn testid={`tv-reset-${r.user_id}`} title="Reset change count" onClick={() => override(r.user_id, "reset_changes", "Change count reset")}>
                        <ArrowsClockwise size={12} /> Reset
                      </ActBtn>
                      {r.lifetime ? (
                        <ActBtn testid={`tv-1yr-${r.user_id}`} title="Switch to 1-year validity" onClick={() => override(r.user_id, "set_one_year", "Set to 1-year validity")}>
                          1 Year
                        </ActBtn>
                      ) : (
                        <ActBtn testid={`tv-lifetime-${r.user_id}`} title="Grant lifetime" onClick={() => override(r.user_id, "set_lifetime", "Lifetime granted")}>
                          <Crown size={12} /> Lifetime
                        </ActBtn>
                      )}
                      <ActBtn testid={`tv-revoke-${r.user_id}`} danger title="Revoke access" onClick={() => override(r.user_id, "revoke", "Access revoked")}>
                        <Trash size={12} /> Revoke
                      </ActBtn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const ManualAddCard = ({ onAdded }) => {
  const [uq, setUq] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [tvId, setTvId] = useState("");
  const [lifetime, setLifetime] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!uq.trim() || selected) { setResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/admin/tradingview/user-search`, { params: { q: uq }, headers: getAuthHeaders() })
        .then(r => setResults(r.data.users)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [uq, selected]);

  const submit = async () => {
    if (!selected || !tvId.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/tradingview/users/${selected.user_id}/manual-set`,
        { tv_username: tvId.trim(), lifetime }, { headers: getAuthHeaders() });
      toast.success(`TradingView ID '${tvId.trim()}' granted to ${selected.name} (${lifetime ? "LIFETIME" : "1 year"})`);
      setSelected(null); setUq(""); setTvId(""); setLifetime(true);
      onAdded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={panel} data-testid="tv-manual-add-card">
      <p className="font-bold text-sm text-white mb-1 flex items-center gap-2">
        <UserPlus size={16} className="text-emerald-400" /> Manually Grant TradingView Access
      </p>
      <p className="text-xs text-slate-400 mb-4">Assign a TradingView ID to any user directly (overwrites their current ID, does NOT consume their 3 changes).</p>
      <div className="grid md:grid-cols-3 gap-3">
        {/* User picker */}
        <div className="relative">
          <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-1">User</label>
          {selected ? (
            <div data-testid="tv-manual-selected-user" className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
              <div className="min-w-0">
                <p className="text-xs font-semibold text-white truncate">{selected.name}</p>
                <p className="text-[10px] text-slate-400 truncate">{selected.display_id} · {selected.email}</p>
              </div>
              <button data-testid="tv-manual-clear-user" onClick={() => { setSelected(null); setUq(""); }} className="text-slate-400 hover:text-white text-xs shrink-0">✕</button>
            </div>
          ) : (
            <>
              <input
                data-testid="tv-manual-user-search"
                value={uq} onChange={e => setUq(e.target.value)}
                placeholder="Search name, email, VAT-ID, phone..."
                className="w-full px-3 py-2 rounded-lg text-xs bg-slate-950 border border-slate-800 text-white outline-none"
              />
              {results.length > 0 && (
                <div className="absolute z-20 mt-1 w-full rounded-lg bg-slate-900 border border-slate-700 shadow-xl max-h-56 overflow-y-auto">
                  {results.map(u => (
                    <button key={u.user_id} data-testid={`tv-manual-pick-${u.user_id}`}
                      onClick={() => { setSelected(u); setResults([]); }}
                      className="w-full text-left px-3 py-2 hover:bg-slate-800 border-b border-slate-800/60 last:border-0">
                      <p className="text-xs font-semibold text-white">{u.name} <span className="text-slate-500 font-normal">{u.display_id}</span></p>
                      <p className="text-[10px] text-slate-400">{u.email}{u.current_tv_username ? ` · current TV: ${u.current_tv_username}` : ""}{!u.fee_paid ? " · fee NOT paid" : ""}</p>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        {/* TV ID */}
        <div>
          <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-1">TradingView Username</label>
          <input
            data-testid="tv-manual-username-input"
            value={tvId} onChange={e => setTvId(e.target.value)}
            placeholder="e.g. trader_velox"
            className="w-full px-3 py-2 rounded-lg text-xs bg-slate-950 border border-slate-800 text-white outline-none"
          />
        </div>
        {/* Validity + submit */}
        <div>
          <label className="text-[10px] uppercase tracking-wide text-slate-500 block mb-1">Validity</label>
          <div className="flex gap-2">
            <button data-testid="tv-manual-lifetime-btn" onClick={() => setLifetime(true)}
              className={`px-3 py-2 rounded-lg text-[10px] font-bold border ${lifetime ? "border-amber-400/50 bg-amber-400/10 text-amber-300" : "border-slate-700 text-slate-400"}`}>
              <Crown size={11} className="inline mr-1" />LIFETIME
            </button>
            <button data-testid="tv-manual-1yr-btn" onClick={() => setLifetime(false)}
              className={`px-3 py-2 rounded-lg text-[10px] font-bold border ${!lifetime ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-300" : "border-slate-700 text-slate-400"}`}>
              1 YEAR
            </button>
            <button data-testid="tv-manual-submit-btn" onClick={submit} disabled={busy || !selected || !tvId.trim()}
              className="flex-1 px-3 py-2 rounded-lg text-[11px] font-bold bg-emerald-500 text-emerald-950 disabled:opacity-40 flex items-center justify-center gap-1">
              <CheckCircle size={13} weight="fill" /> {busy ? "Adding..." : "Grant Access"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value, onCopy, link }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-slate-400 w-24 shrink-0">{label}</span>
    {link ? (
      <a href={value} target="_blank" rel="noreferrer" className="font-mono text-cyan-300 truncate flex-1 hover:underline">{value}</a>
    ) : (
      <span className="font-mono text-slate-200 truncate flex-1">{value}</span>
    )}
    <button onClick={onCopy} className="text-slate-400 hover:text-white"><Copy size={14} /></button>
  </div>
);

const ActBtn = ({ children, onClick, danger, title, testid }) => (
  <button data-testid={testid} title={title} onClick={onClick}
    className={`px-2 py-1 rounded-md text-[10px] font-bold flex items-center gap-1 border transition-colors ${
      danger ? "border-rose-500/40 text-rose-300 hover:bg-rose-500/10" : "border-slate-700 text-slate-300 hover:bg-slate-800"
    }`}>
    {children}
  </button>
);
